import { defineStore } from 'pinia';
import apiClient from '@/api/index';
import type { AuthState } from '@/types';

export const useAuthStore = defineStore('auth', {

    state: (): AuthState => ({
        user: localStorage.getItem('asl_user') ? JSON.parse(localStorage.getItem('asl_user')!) : null,
        currentOrganization: localStorage.getItem('asl_current_organization') ? JSON.parse(localStorage.getItem('asl_current_organization')!) : null,
        accessibleOrganizations: [],
        loading: false,
        isAuthenticated: false,
        error: null,
    }),

    getters: {
        hasPermission: (state) => (permission: string) => {
            return state.user?.permissions?.includes(permission) || false;
        },
        hasRole: (state) => (role: string) => {
            return state.user?.role === role;
        },
        isSuperAdmin: (state) => state.user?.role === 'admin',
        hasMultipleOrganizations: (state) => state.accessibleOrganizations.length > 1,
    },


    actions: {

        async login(credentials: any) {
            this.loading = true;
            this.error = null;
            try {
                const response = await apiClient.post('/auth/login/', credentials);
                const { user, access, refresh, selected_organization, accessible_organizations } = response.data;

                this.user = user;
                this.isAuthenticated = true;
                this.currentOrganization = selected_organization;
                this.accessibleOrganizations = accessible_organizations;

                localStorage.setItem('asl_access_token', access);
                localStorage.setItem('asl_refresh_token', refresh);
                localStorage.setItem('asl_user', JSON.stringify(user));
                localStorage.setItem('asl_current_organization', JSON.stringify(selected_organization));
                localStorage.setItem('asl_accessible_organizations', JSON.stringify(accessible_organizations));

                return true;
            } catch (err: any) {
                this.error = err.response?.data?.detail || 'Ошибка входа. Проверьте учетные данные.';
                throw err;
            } finally {
                this.loading = false;
            }

        },

        async updateProfile(profileData: any) {
            this.loading = true;
            this.error = null;
            try {
                const response = await apiClient.patch('/user/', profileData);
                this.user = response.data;
                localStorage.setItem('asl_user', JSON.stringify(this.user));
                return true;
            } catch (err: any) {
                this.error = err.response?.data?.detail || 'Ошибка обновления профиля.';
                console.error('Update profile error:', err);
                return false;
            } finally {
                this.loading = false;
            }
        },

        async attachTimestamp(invoice: string, pkcs7_64: any, signature_hex: any) {
            this.loading = true;
            this.error = null;
            try {
                const response = await apiClient.post('/integrations/didox/attach-timestamp/', { invoice, pkcs7_64, signature_hex });
                console.log('Timestamp attached successfully:', response.data);
                return response.data;
            } catch (err: any) {
                this.error = err.response?.data?.detail || 'Ошибка прикрепления временной метки.';
                console.error('Attach timestamp error:', err);
                return err.response.data;
            } finally {
                this.loading = false;
            }
        },

        async fetchUser() {
            this.loading = true;
            this.error = null;
            try {
                const response = await apiClient.get('/user/');
                this.user = response.data;
                localStorage.setItem('asl_user', JSON.stringify(this.user));
                return true;
            } catch (err: any) {
                this.error = err.response?.data?.detail || 'Ошибка при загрузке данных пользователя.';
                console.error('Fetch user error:', err);
                return false;
            } finally {
                this.loading = false;
            }
        },

        async fetchOrganizations(params: Record<string, any> = {}) {
            this.loading = true;
            this.error = null;
            try {
                const response = await apiClient.get('/organizations/?limit=1000', { params });
                this.accessibleOrganizations = response.data.results;
                return true;
            } catch (err: any) {
                this.error = err.response?.data?.detail || 'Ошибка при загрузке доступных организаций.';
                console.error('Fetch organizations error:', err);
                return false;
            } finally {
                this.loading = false;
            }
        },

        async switchOrganization(organization_id: string) {
            this.loading = true;
            this.error = null;
            try {
                const response = await apiClient.post(`/auth/organization/switch/`, { organization_id });
                this.currentOrganization = response.data.organization;
                localStorage.setItem('asl_current_organization', JSON.stringify(this.currentOrganization));
                return true;
            } catch (err: any) {
                this.error = err.response?.data?.detail || 'Ошибка переключения организации.';
                console.error('Switch organization error:', err);
                return false;
            } finally {
                this.loading = false;
            }
        },
    }
})